import React, { memo, useState, useCallback, useEffect, type Ref } from 'react';
import debounce from 'lodash/debounce';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import {
	type UIAnalyticsEvent,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';

import { fg } from '@atlassian/jira-feature-gating';
import { PACKAGE_NAME, TEAM_NAME } from '../../constants.tsx';
import messages from './messages.tsx';

type Props = {
	cellType: string;
	reason: string | Error;
};

type ErrorAnalyticsFn = (cellType: string, reason: string | Error, event: UIAnalyticsEvent) => void;
const fireAnalyticsCache: Record<string, ErrorAnalyticsFn> = {};

const fireAnalyticsDebouncedByCellType: ErrorAnalyticsFn = (
	cellType: string,
	reason: string | Error,
	event: UIAnalyticsEvent,
) => {
	if (!(cellType in fireAnalyticsCache)) {
		fireAnalyticsCache[cellType] = debounce(fireAnalytics);
	}
	fireAnalyticsCache[cellType](cellType, reason, event);
};
const fireAnalytics: ErrorAnalyticsFn = (
	cellType: string,
	reason: string | Error,
	event: UIAnalyticsEvent,
) => {
	const options = reason instanceof Error ? { cause: reason } : undefined;
	const error = new Error(
		`Failed to render cell data for the ${cellType} cell type. Reason: ${reason}`,
		options,
	);
	fireErrorAnalytics({
		meta: {
			id: 'errorCell',
			packageName: PACKAGE_NAME,
			teamName: TEAM_NAME,
		},
		error,
		event,
		attributes: {
			cellType,
		},
		sendToPrivacyUnsafeSplunk: true,
	});

	// Clean up debounced function after flush
	delete fireAnalyticsCache[cellType];
};

const ErrorCell = ({ cellType, reason }: Props) => {
	const { formatMessage } = useIntl();
	const [isHovered, setIsHovered] = useState(false);

	const onMouseEnter = useCallback(() => setIsHovered(true), []);
	const onMouseLeave = useCallback(() => setIsHovered(false), []);

	const iconColor = isHovered ? token('color.icon') : token('color.icon.subtle');

	const message = formatMessage(messages.tooltip);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (!fg('empanada_error_cell_logging')) {
			return;
		}
		const timerId = setTimeout(() =>
			fireAnalyticsDebouncedByCellType(cellType, reason, createAnalyticsEvent({})),
		);
		// Cancel any pending events on unmount. This prevents sending an error event when the cell was mounted
		// due to an intermediary render. We've observed this when new columns are committed to the Relay store
		// in a separate render before issue field sets are aligned.
		return () => clearTimeout(timerId);

		// Only fire on mount

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (fg('empanada_error_cell_logging')) {
			return;
		}
		const sendAnalytics = () => {
			const error = new Error(
				`Failed to render cell data for the ${cellType} cell type. Reason: ${reason}`,
			);
			fireErrorAnalytics({
				meta: {
					id: 'errorCell',
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				error,
				event: createAnalyticsEvent({}),
				attributes: {
					cellType,
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		};
		const sendAnalyticsDebounced = debounce(sendAnalytics);
		sendAnalyticsDebounced();
		// Cancel any debounced events on unmount. This prevents sending an error event when the cell was mounted
		// due to an intermediary render. We've observed this when new columns are committed to the Relay store
		// in a separate render before issue field sets are aligned.
		return () => {
			sendAnalyticsDebounced.cancel();
		};
		// Only fire on mount

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const cellContent = (ref: Ref<HTMLDivElement> = null) => (
		<div
			// eslint-disable-next-line jira/integration/test-id-by-folder-structure
			data-testid="native-issue-table.common.error-cell.tooltip-target"
			// eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
			onMouseEnter={onMouseEnter}
			// eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
			onMouseLeave={onMouseLeave}
			ref={ref}
		>
			<WarningIcon color={iconColor} label={message} spacing="spacious" />
		</div>
	);

	return (
		<Tooltip content={message} position="bottom-start">
			{cellContent()}
		</Tooltip>
	);
};

export default memo<Props>(ErrorCell);
